import React, { useState, useEffect, memo, useRef } from 'react'
import { Button, Tag, Collapse, Spin } from 'antd'
import { SearchOutlined, LikeOutlined, DislikeOutlined } from '@ant-design/icons'
import { AIAssistantSopNode, CallInfo, AIAssistantNodeType, AIAssistantSearchNode, AIAssistantSopNodeType } from 'src/types/ai-assistant'
import CallWorkbenchContainer from '../useCallWorkbenchHook'
import { throttle } from 'lodash'
import { UserInfo } from '@types'
import { getStaffName } from '@library'
import { ModuleNameMapping } from '@configs/constants'
import MarkdownItem from './MarkdownItem'

const { Panel } = Collapse

// 公共的脚本内容操作区域组件
interface ScriptContentActionsProps {
  item: AIAssistantSopNode
  sopNodeLoading?: boolean
  selectedAction?: 'like' | 'dislike' | null
  onAction: (scriptId: string, isLike: boolean, type: AIAssistantNodeType) => void
}

const ScriptContentActions: React.FC<ScriptContentActionsProps> = ({
  item,
  sopNodeLoading = false,
  selectedAction = null,
  onAction,
}) => {
  // 解析关键词字符串为数组
  const parseKeywords = (keywords: string): string[] => {
    if (!keywords) return []
    return keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword)
  }

  return (
    <>
      <div className="script-text">{item?.words}</div>
      {parseKeywords(item?.keywords).length > 0 && (
        <div className="script-tags">
          {parseKeywords(item?.keywords).map((tag, index) => (
            <Tag key={index} className="script-tag">{tag}</Tag>
          ))}
        </div>
      )}
      <div className="script-actions">
        <Button
          type={selectedAction === 'like' ? 'link' : 'text'}
          icon={<LikeOutlined />}
          size="small"
          disabled={sopNodeLoading}
          onClick={() => onAction(item?.id, true, AIAssistantNodeType.assistant)}
        />
        <div className="script-action-divider" />
        <Button
          type={selectedAction === 'dislike' ? 'link' : 'text'}
          icon={<DislikeOutlined />}
          size="small"
          disabled={sopNodeLoading}
          onClick={() => onAction(item?.id, false, AIAssistantNodeType.assistant)}
        />
      </div>
    </>
  )
}

const AIContent: React.FC<{ callInfo: CallInfo, userInfo: UserInfo }> = memo(({ callInfo, userInfo }) => {
  const { getSopNode, nodeList, setNodeList, sopNodeLoading, getAISearchNode, searchNodeLoading, setSearchNodeLoading } = CallWorkbenchContainer.useContainer()
  const [searchText, setSearchText] = useState('')
  const [selectedActions, setSelectedActions] = useState<{ [key: string]: 'like' | 'dislike' | null }>({})
  const scriptContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    getSopNode({ nodeId: undefined, leadId: callInfo?.leadId, cid: callInfo?.cid })
    return () => {
      setNodeList([])
      setSearchNodeLoading(false)
    }
  }, [])

  const handleSearch = () => {
    // 处理AI检索逻辑
    getAISearchNode({ type: 'search', content: searchText })
    // eslint-disable-next-line no-unused-expressions
    window.XSY_TRACK?.trackClick('communication_assistant', {
      content: {
        belong_module: userInfo?.belongModule,
        belong_module_name: ModuleNameMapping[userInfo?.belongModule],
        follower: getStaffName(userInfo.salesName, userInfo.salesNameCh),
        communication_assistant_record_id: callInfo?.recordId,
        communication_assistant_lead_id: callInfo?.leadId,
        communication_assistant_cid: callInfo?.cid,
        communication_assistant_type: 'manualSearch',
        communication_assistant_value: searchText,
      },
    })
  }

  const handleOptionClick = throttle(async (child: AIAssistantSopNode) => {
    try {
      await getSopNode({
        nodeId: child?.id,
        leadId: callInfo?.leadId,
        cid: callInfo?.cid,
      })

      // eslint-disable-next-line no-unused-expressions
      window.XSY_TRACK?.trackClick('communication_assistant', {
        content: {
          belong_module: userInfo?.belongModule,
          belong_module_name: ModuleNameMapping[userInfo?.belongModule],
          follower: getStaffName(userInfo.salesName, userInfo.salesNameCh),
          communication_assistant_record_id: callInfo?.recordId,
          communication_assistant_lead_id: callInfo?.leadId,
          communication_assistant_cid: callInfo?.cid,
          communication_assistant_value: child.name,
          communication_assistant_branch_id: child?.id,
          communication_assistant_type: 'branch',
        },
      })

      // 手动触发滚动到底部
      if (scriptContentRef.current) {
        const maxScrollTop = scriptContentRef.current.scrollHeight - scriptContentRef.current.clientHeight
        scriptContentRef.current.scrollTo({
          top: maxScrollTop,
          behavior: 'smooth',
        })
      }
    } catch (error) {
      console.error('处理选项时出错:', error)
    }
  }, 1000, { leading: true, trailing: false })

  // 统一的点赞/点踩处理方法
  const handleAction = (scriptId: string, isLike: boolean, type: AIAssistantNodeType) => {
    const actionType = isLike ? 'like' : 'dislike'

    setSelectedActions(prev => ({
      ...prev,
      [scriptId]: prev[scriptId] === actionType ? null : actionType,
    }))

    window.XSY_TRACK?.trackClick('communication_assistant', {
      content: {
        belong_module: userInfo?.belongModule,
        belong_module_name: ModuleNameMapping[userInfo?.belongModule],
        follower: getStaffName(userInfo.salesName, userInfo.salesNameCh),
        communication_assistant_record_id: callInfo?.recordId,
        communication_assistant_lead_id: callInfo?.leadId,
        communication_assistant_cid: callInfo?.cid,
        communication_assistant_value: isLike ? 'like' : 'dislike',
        communication_assistant_branch_id: scriptId,
        communication_assistant_type: 'thumbsUp',
      },
    })
  }

  // 渲染branch类型的内容
  const renderBranchContent = (item: AIAssistantSopNode) => {
    if (!item) {
      return null
    }
    const children = 'children' in item ? item?.children : []
    const childrenTitle = 'childrenTitle' in item ? item?.childrenTitle : ''

    return (
      <div className="script-content">
        <ScriptContentActions
          item={item}
          sopNodeLoading={sopNodeLoading}
          selectedAction={selectedActions[item?.id]}
          onAction={handleAction}
        />
        {children && children.length > 0 && (
          <div className="script-children">
            {childrenTitle && (
              <div className="script-children-title">客户反馈{childrenTitle}</div>
            )}
            <div className="script-options">
              {children.map((child, index) => (
                <Button
                  key={index}
                  size="small"
                  disabled={sopNodeLoading}
                  className="script-option-btn"
                  onClick={() => handleOptionClick(child)}
                >
                  {child?.name}
                </Button>
              ))}
            </div>
          </div>
        )}

      </div>
    )
  }

  // 渲染task类型的内容
  const renderTaskContent = (item: AIAssistantSopNode) => {
    if (!item?.children || item?.children?.length === 0) {
      return (
        <div className="script-content">
          <ScriptContentActions
            item={item}
            sopNodeLoading={sopNodeLoading}
            selectedAction={selectedActions[item?.id]}
            onAction={handleAction}
          />
        </div>
      )
    }

    return (
      <div className="script-task-content">
        <Collapse
          defaultActiveKey={[item?.children[0]?.id]}
          className="script-collapse"
        >
          {item?.children?.map(child => (
            <Panel
              collapsible="header"
              key={child?.id}
              header={
                <div className="script-panel-header" onClick={() => {
                  // eslint-disable-next-line no-unused-expressions
                  window.XSY_TRACK?.trackClick('communication_assistant', {
                    content: {
                      belong_module: userInfo?.belongModule,
                      belong_module_name: ModuleNameMapping[userInfo?.belongModule],
                      follower: getStaffName(userInfo.salesName, userInfo.salesNameCh),
                      communication_assistant_record_id: callInfo?.recordId,
                      communication_assistant_lead_id: callInfo?.leadId,
                      communication_assistant_cid: callInfo?.cid,
                      communication_assistant_value: child.name,
                      communication_assistant_branch_id: child?.id,
                      communication_assistant_type: 'task',
                    },
                  })
                }}>
                  <span className="script-panel-title">{child?.name}</span>
                </div>
              }
              className="script-panel"
            >
              {/* 列表下面还是列表 */}
              {(!child?.children || child?.children?.length === 0) ? (
                <div className="script-content">
                  <ScriptContentActions
                    item={child}
                    sopNodeLoading={sopNodeLoading}
                    selectedAction={selectedActions[child?.id]}
                    onAction={handleAction}
                  />
                </div>
              ) : child?.type === AIAssistantSopNodeType.task ? (
                renderTaskContent(child)
              ) : (
                renderBranchContent(child)
              )}
            </Panel>
          ))}
        </Collapse>
      </div>
    )
  }

  // 渲染话术内容
  const renderScriptContent = (script: AIAssistantSopNode, index: number) => {
    return (
      <div key={`${script?.id}-${index}`} className="script-item">
        <div className="script-header">
          <div className="script-title">话术助手：{script?.label || script?.name}</div>
        </div>
        {script?.type === AIAssistantSopNodeType.task ? renderTaskContent(script) : renderBranchContent(script)}
      </div>
    )
  }

  const renderMarkdownContent = (script: AIAssistantSearchNode, index: number) => {
    return (
      <div key={`${script?.id}-${index}`} className="script-item">
        <div className="script-header">
          <div className="script-title">AI问答:{(script?.label && script?.label?.length > 32) ? script.label.substring(0, 32) + '...' : script?.label}{!script.done && <Spin size="small" spinning={searchNodeLoading} style={{ marginLeft: '6px' }} />}</div>
        </div>
        <div className="script-content">
          <MarkdownItem content={script.words} id={script.id} />
          <div className="script-actions">
            <Button
              type={selectedActions[script?.id] === 'like' ? 'link' : 'text'}
              icon={<LikeOutlined />}
              size="small"
              disabled={sopNodeLoading}
              onClick={() => handleAction(script?.id, true, AIAssistantNodeType.aiSearch)}
            />
            <div className="script-action-divider" />
            <Button
              type={selectedActions[script?.id] === 'dislike' ? 'link' : 'text'}
              icon={<DislikeOutlined />}
              size="small"
              disabled={sopNodeLoading}
              onClick={() => handleAction(script?.id, false, AIAssistantNodeType.aiSearch)}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="ai-content">
      {/* AI检索输入框 */}
      <div className="ai-search-container">
        <div className="ai-search-input">
          <input
            type="text"
            placeholder="输入通话文本/产品关键词/行业资讯，一键 AI 检索"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="ai-search-field"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            className="ai-search-btn"
            disabled={searchNodeLoading}
          >
            AI 问答
          </Button>
        </div>
      </div>
      <div className="ai-script-header">
        <span className="ai-script-title">💡 AI 实时话术助手</span>
      </div>
      {/* AI实时话术助手 */}
      <div className="ai-script-assistant">
        <div className="ai-script-content" ref={scriptContentRef}>
          {nodeList.map((script, index) => {
            return script.type === AIAssistantNodeType.assistant
              ? renderScriptContent(script?.node as AIAssistantSopNode, index)
              : renderMarkdownContent(script.node as AIAssistantSearchNode, index)
          })}
        </div>
        <div style={{ height: 40 }}>
          <Spin spinning={sopNodeLoading} style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '10px',
          }} />
        </div>
      </div>
    </div>
  )
})

export default AIContent
